import { Button, Modal, Select, Stack, Text } from "@mantine/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { candidatesApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { axioTokens } from "../../theme";
import { LoadingOverlay } from "../ui";
import { PrepGuideError, PrepGuideSections } from "./PrepGuideSections";

const MODAL_TITLE = "Submission prep guide";
const JOB_SELECT_LABEL = "Job";
const CANDIDATE_SELECT_LABEL = "Candidate";
const GENERATE_LABEL = "Generate briefing";
const PICK_JOB_PLACEHOLDER = "Select a job";
const PICK_CANDIDATE_PLACEHOLDER = "Select a candidate";
export const NO_MATCHING_JOBS_COPY = "This candidate does not match any job currently.";
const PREP_NOT_A_SUBMISSION_COPY = "The briefing is specific to this candidate and job. Nothing is submitted from here.";
const PREP_GENERATE_ERROR = "Could not generate the briefing.";

interface Props {
  opened: boolean;
  onClose: () => void;
  candidateId?: string | null;
  jobId?: string | null;
  candidateOptions?: { value: string; label: string }[];
  jobOptions?: { value: string; label: string }[];
}

export function SubmissionPrepModal({
  opened,
  onClose,
  candidateId,
  jobId,
  candidateOptions = [],
  jobOptions = [],
}: Props) {
  const [pickedCandidateId, setPickedCandidateId] = useState<string | null>(candidateId ?? null);
  const [pickedJobId, setPickedJobId] = useState<string | null>(jobId ?? null);

  const resolvedCandidateId = candidateId ?? pickedCandidateId;
  const resolvedJobId = jobId ?? pickedJobId;
  const needsCandidate = !candidateId;
  const needsJob = !jobId;

  const topMatchesQuery = useQuery({
    queryKey: ["candidate-matches", resolvedCandidateId],
    queryFn: () => candidatesApi.matches(resolvedCandidateId!),
    enabled: opened && !!resolvedCandidateId && needsJob,
  });

  const mergedJobOptions = useMemo(() => {
    if (jobOptions.length > 0) return jobOptions;
    return (topMatchesQuery.data ?? []).map((match) => ({
      value: match.job_id,
      label: match.job_title,
    }));
  }, [jobOptions, topMatchesQuery.data]);

  const prepMutation = useMutation({
    mutationFn: () => candidatesApi.interviewPrep(resolvedCandidateId!, { job_id: resolvedJobId! }),
  });

  const canGenerate = !!resolvedCandidateId && !!resolvedJobId;
  const noMatchingJobs =
    needsJob &&
    jobOptions.length === 0 &&
    !topMatchesQuery.isLoading &&
    !topMatchesQuery.isError &&
    mergedJobOptions.length === 0;
  const errorMessage =
    prepMutation.error instanceof ApiError ? prepMutation.error.message : prepMutation.isError ? PREP_GENERATE_ERROR : null;

  function handleClose() {
    prepMutation.reset();
    if (!candidateId) setPickedCandidateId(null);
    if (!jobId) setPickedJobId(null);
    onClose();
  }

  return (
    <Modal opened={opened} onClose={handleClose} title={MODAL_TITLE} size="lg">
      <Stack gap={axioTokens.unit[32]}>
        {needsCandidate && (
          <Select
            label={CANDIDATE_SELECT_LABEL}
            placeholder={PICK_CANDIDATE_PLACEHOLDER}
            data={candidateOptions}
            value={pickedCandidateId}
            onChange={setPickedCandidateId}
            searchable
          />
        )}
        {needsJob && noMatchingJobs ? (
          <Text size="sm" c="dimmed">
            {NO_MATCHING_JOBS_COPY}
          </Text>
        ) : null}
        {needsJob && !noMatchingJobs ? (
          <Select
            label={JOB_SELECT_LABEL}
            placeholder={PICK_JOB_PLACEHOLDER}
            data={mergedJobOptions}
            value={pickedJobId}
            onChange={setPickedJobId}
            searchable
          />
        ) : null}
        {!prepMutation.data && (
          <Button disabled={!canGenerate} loading={prepMutation.isPending} onClick={() => prepMutation.mutate()}>
            {GENERATE_LABEL}
          </Button>
        )}
        {prepMutation.isPending && !prepMutation.data ? <LoadingOverlay height="var(--axio-unit-200)" /> : null}
        {errorMessage ? <PrepGuideError message={errorMessage} /> : null}
        {prepMutation.data ? <PrepGuideSections prep={prepMutation.data} /> : null}
        {!needsJob && !needsCandidate ? null : (
          <Text size="sm" c="dimmed">
            {PREP_NOT_A_SUBMISSION_COPY}
          </Text>
        )}
      </Stack>
    </Modal>
  );
}